// Admin → Catalogue → PDF Import.
//
// Three-stage pipeline that turns a supplier's PDF price list into rows in the
// generic catalogue store (catalogue_products), via /api/admin/pdf-import:
//
//   Stage 1  Upload & Extract — pick a catalogue supplier, choose the TKOS
//            schema columns to extract, optionally add AI instructions, drop a
//            PDF, and call Anthropic (POST /extract). GROUP_NAME is always the
//            supplier name (stamped server-side), never inferred.
//   Stage 2  Preview — tabular preview of the first rows + extraction log +
//            Download CSV ({supplier-name}-catalogue.csv).
//   Stage 3  Import — Dry Run (POST /dry-run) reports ranges/count/quality with
//            no writes; Confirm & Import (POST /import) is disabled until a dry
//            run passes.
//
// Reached from the sidebar (catalogue cluster) — see shell/pages-registry.ts.

import { api } from "@tkos/api-client";

import { registerPage } from "../../shell/router";
import type { Page, PageContext, PageCleanup } from "../../shell/types";

import "./styles.css";

// ── Schema ─────────────────────────────────────────────────────────────────

const ALL_COLUMNS = [
  "GROUP_NAME", "product_code", "description",
  "width", "height", "depth", "handing", "colour", "finish",
  "trade_price", "rrp", "unit", "notes",
] as const;
type Column = (typeof ALL_COLUMNS)[number];

const REQUIRED_COLUMNS: Column[] = ["GROUP_NAME", "product_code", "description"];
const DEFAULT_ON: Column[] = [
  "GROUP_NAME", "product_code", "description", "width", "height", "depth",
  "colour", "finish", "trade_price",
];

const MAX_PDF_BYTES = 50 * 1024 * 1024;

type ProductRow = Record<string, unknown>;

interface SupplierOpt { id: string; slug: string; name: string; }

interface ExtractResp {
  ok: boolean;
  supplierName: string;
  columns: Column[];
  count: number;
  products: ProductRow[];
  warnings: string[];
  model?: string;
}

interface DryReport {
  totalRows: number;
  productCount: number;
  errorCount: number;
  rangesFound: string[];
  dataQuality: string[];
  sampleErrors: { rowNum: number; reason: string }[];
}

interface ImportResp {
  ok: boolean;
  count: number;
  errorCount: number;
  csvImportId: string;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function slugify(s: string): string {
  return s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "supplier";
}

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function csvCell(v: unknown): string {
  if (v == null) return "";
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function buildCsv(products: ProductRow[], columns: Column[]): string {
  const header = columns.join(",");
  const lines = products.map((p) => columns.map((c) => csvCell(p[c])).join(","));
  return [header, ...lines].join("\r\n");
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K, cls?: string, text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (cls) node.className = cls;
  if (text != null) node.textContent = text;
  return node;
}

// ── Page ─────────────────────────────────────────────────────────────────────

const page: Page = {
  name: "catalogue-pdf-import",
  label: "PDF Import",
  icon: "📄",
  mount(container: HTMLElement, ctx: PageContext): PageCleanup {
    // ── State ────────────────────────────────────────────────────────────────
    const selected = new Set<Column>(DEFAULT_ON);
    REQUIRED_COLUMNS.forEach((c) => selected.add(c));

    let suppliers: SupplierOpt[] = [];
    let supplierId = "";
    let supplierName = "";
    let file: File | null = null;
    let extracting = false;
    let extracted: ExtractResp | null = null;
    let dryReport: DryReport | null = null;
    let dryPassed = false;
    let importing = false;
    let dryRunning = false;
    let importResult: ImportResp | null = null;

    const ac = new AbortController();

    // Invalidate everything downstream of a new extraction.
    function resetAfterExtract(): void {
      extracted = null;
      dryReport = null;
      dryPassed = false;
      importResult = null;
    }
    function resetAfterDryInputs(): void {
      dryReport = null;
      dryPassed = false;
      importResult = null;
    }

    // ── Render ────────────────────────────────────────────────────────────────
    function render(): void {
      container.replaceChildren();

      const wrap = el("section", "apdf-wrap");

      // Header
      const head = el("header", "apdf-head");
      head.appendChild(el("h1", undefined, "PDF Price List Importer"));
      head.appendChild(el("p", "apdf-sub",
        "Extract a supplier's PDF price list into their catalogue. Every row is filed under the supplier name (GROUP_NAME). A dry run is required before import."));
      wrap.appendChild(head);

      wrap.appendChild(renderStage1());
      if (extracted) wrap.appendChild(renderStage2());
      if (extracted) wrap.appendChild(renderStage3());

      container.appendChild(wrap);
    }

    // ── Stage 1 ─────────────────────────────────────────────────────────────
    function renderStage1(): HTMLElement {
      const card = el("div", "adm-card apdf-card");
      card.appendChild(el("div", "apdf-stage-tag", "Stage 1 · Upload & Extract"));

      // Supplier picker
      const supRow = el("div", "apdf-field");
      supRow.appendChild(el("label", "apdf-label", "Catalogue supplier"));
      const sel = el("select", "adm-select");
      sel.disabled = suppliers.length === 0;
      const ph = el("option", undefined, suppliers.length ? "Select a supplier…" : "Loading suppliers…");
      ph.value = "";
      sel.appendChild(ph);
      for (const s of suppliers) {
        const o = el("option", undefined, s.name);
        o.value = s.id;
        if (s.id === supplierId) o.selected = true;
        sel.appendChild(o);
      }
      sel.addEventListener("change", () => {
        supplierId = sel.value;
        const sup = suppliers.find((s) => s.id === supplierId);
        // Supplier name drives GROUP_NAME; default to the picked supplier's name.
        supplierName = sup ? sup.name : "";
        resetAfterExtract();
        render();
      });
      supRow.appendChild(sel);
      card.appendChild(supRow);

      // Supplier name → GROUP_NAME
      const nameRow = el("div", "apdf-field");
      nameRow.appendChild(el("label", "apdf-label", "Supplier name (used as GROUP_NAME for every row)"));
      const nameInput = el("input", "adm-input");
      nameInput.type = "text";
      nameInput.value = supplierName;
      nameInput.placeholder = "e.g. Omega Kitchens";
      nameInput.addEventListener("input", () => {
        supplierName = nameInput.value;
        resetAfterDryInputs();
        // light refresh of buttons only — avoid re-render to keep focus
        syncButtons();
      });
      nameRow.appendChild(nameInput);
      card.appendChild(nameRow);

      // Column selector
      const colsRow = el("div", "apdf-field");
      colsRow.appendChild(el("label", "apdf-label", "Columns to extract"));
      const chips = el("div", "apdf-chips");
      for (const col of ALL_COLUMNS) {
        const required = REQUIRED_COLUMNS.includes(col);
        const chip = el("label", "apdf-chip");
        const cb = el("input");
        cb.type = "checkbox";
        cb.checked = selected.has(col);
        cb.disabled = required;
        if (required) chip.classList.add("is-required");
        if (cb.checked) chip.classList.add("is-on");
        cb.addEventListener("change", () => {
          if (cb.checked) selected.add(col); else selected.delete(col);
          resetAfterExtract();
          render();
        });
        chip.appendChild(cb);
        chip.appendChild(el("span", undefined, col + (required ? " *" : "")));
        chips.appendChild(chip);
      }
      colsRow.appendChild(chips);
      card.appendChild(colsRow);

      // Instructions
      const insRow = el("div", "apdf-field");
      insRow.appendChild(el("label", "apdf-label", "Additional instructions for the AI (optional)"));
      const ins = el("textarea", "adm-textarea");
      ins.rows = 3;
      ins.placeholder = "e.g. Prices are trade ex-VAT. Ignore the discount table on the last page.";
      ins.id = "apdf-ins";
      insRow.appendChild(ins);
      card.appendChild(insRow);

      // Drop zone
      const drop = el("div", "apdf-drop");
      drop.id = "apdf-drop";
      function paintDrop(): void {
        drop.replaceChildren();
        if (file) {
          drop.appendChild(el("div", "apdf-file-name", file.name));
          drop.appendChild(el("div", "apdf-file-meta", fmtBytes(file.size)));
          const clear = el("button", "adm-btn-ghost apdf-clear", "Remove");
          clear.type = "button";
          clear.addEventListener("click", (e) => {
            e.stopPropagation();
            file = null;
            resetAfterExtract();
            render();
          });
          drop.appendChild(clear);
        } else {
          drop.appendChild(el("div", "apdf-drop-title", "Drop a PDF here, or click to browse"));
          drop.appendChild(el("div", "apdf-drop-hint", "Max 50 MB · PDF only"));
        }
      }
      const fileInput = el("input");
      fileInput.type = "file";
      fileInput.accept = "application/pdf,.pdf";
      fileInput.hidden = true;
      const acceptFile = (f: File | undefined | null): void => {
        if (!f) return;
        const okType = f.type === "application/pdf" || /\.pdf$/i.test(f.name);
        if (!okType) { ctx.showToast("File must be a PDF", "error"); return; }
        if (f.size > MAX_PDF_BYTES) { ctx.showToast(`PDF too large — max ${fmtBytes(MAX_PDF_BYTES)}`, "error"); return; }
        file = f;
        resetAfterExtract();
        render();
      };
      drop.addEventListener("click", () => fileInput.click());
      fileInput.addEventListener("change", () => acceptFile(fileInput.files?.[0]));
      drop.addEventListener("dragover", (e) => { e.preventDefault(); drop.classList.add("is-drag"); });
      drop.addEventListener("dragleave", () => drop.classList.remove("is-drag"));
      drop.addEventListener("drop", (e) => {
        e.preventDefault();
        drop.classList.remove("is-drag");
        acceptFile(e.dataTransfer?.files?.[0]);
      });
      paintDrop();
      card.appendChild(drop);
      card.appendChild(fileInput);

      // Extract button
      const actions = el("div", "apdf-actions");
      const extractBtn = el("button", "adm-btn apdf-extract", extracting ? "Extracting…" : "Extract");
      extractBtn.type = "button";
      extractBtn.disabled = extracting || !file || !supplierId || !supplierName.trim();
      extractBtn.addEventListener("click", () => void runExtract());
      actions.appendChild(extractBtn);
      card.appendChild(actions);

      return card;
    }

    // ── Stage 2 — preview ─────────────────────────────────────────────────────
    function renderStage2(): HTMLElement {
      const card = el("div", "adm-card apdf-card");
      card.appendChild(el("div", "apdf-stage-tag", "Stage 2 · Preview"));
      const data = extracted!;

      // Extraction log
      const log = el("div", "apdf-log");
      log.appendChild(el("div", "apdf-log-line", `Extracted ${data.count} product${data.count === 1 ? "" : "s"}.`));
      log.appendChild(el("div", "apdf-log-line", `GROUP_NAME stamped as “${data.supplierName}” on every row.`));
      if (data.model) log.appendChild(el("div", "apdf-log-line apdf-muted", `Model: ${data.model}`));
      for (const w of data.warnings || []) {
        log.appendChild(el("div", "apdf-log-line apdf-warn", `⚠ ${w}`));
      }
      if (data.count === 0) {
        log.appendChild(el("div", "apdf-log-line apdf-warn", "No products extracted — check the PDF and instructions, then re-extract."));
      }
      card.appendChild(log);

      // Preview table (first 10 rows)
      if (data.count > 0) {
        const cols = data.columns;
        const previewCount = Math.min(10, data.products.length);
        const tableWrap = el("div", "apdf-table-wrap");
        const table = el("table", "adm-table apdf-table");
        const thead = el("thead");
        const htr = el("tr");
        for (const c of cols) htr.appendChild(el("th", undefined, c));
        thead.appendChild(htr);
        table.appendChild(thead);
        const tbody = el("tbody");
        for (let i = 0; i < previewCount; i++) {
          const p = data.products[i];
          const tr = el("tr");
          for (const c of cols) {
            const v = p[c];
            tr.appendChild(el("td", undefined, v == null ? "—" : String(v)));
          }
          tbody.appendChild(tr);
        }
        table.appendChild(tbody);
        tableWrap.appendChild(table);
        card.appendChild(tableWrap);
        card.appendChild(el("div", "apdf-table-foot",
          `Showing ${previewCount} of ${data.count} rows.`));
      }

      // Download CSV
      const actions = el("div", "apdf-actions");
      const dl = el("button", "adm-btn-ghost", "Download CSV");
      dl.type = "button";
      dl.disabled = data.count === 0;
      dl.addEventListener("click", () => downloadCsv());
      actions.appendChild(dl);
      card.appendChild(actions);

      return card;
    }

    // ── Stage 3 — dry run + import ──────────────────────────────────────────────
    function renderStage3(): HTMLElement {
      const card = el("div", "adm-card apdf-card");
      card.appendChild(el("div", "apdf-stage-tag", "Stage 3 · Import"));
      const data = extracted!;

      if (data.count === 0) {
        card.appendChild(el("p", "apdf-muted", "Nothing to import — extract some rows first."));
        return card;
      }

      const actions = el("div", "apdf-actions");
      const dryBtn = el("button", "adm-btn-ghost apdf-dry", dryRunning ? "Running dry run…" : "Dry Run");
      dryBtn.type = "button";
      dryBtn.disabled = dryRunning || importing;
      dryBtn.addEventListener("click", () => void runDry());
      actions.appendChild(dryBtn);

      const importBtn = el("button", "adm-btn apdf-import", importing ? "Importing…" : "Confirm & Import");
      importBtn.type = "button";
      importBtn.disabled = !dryPassed || importing || dryRunning;
      importBtn.title = dryPassed ? "" : "Run a successful dry run first";
      importBtn.addEventListener("click", () => void runImport());
      actions.appendChild(importBtn);
      card.appendChild(actions);

      // Dry-run report
      if (dryReport) {
        const r = dryReport;
        const rep = el("div", "apdf-report");
        rep.appendChild(el("div", "apdf-report-title", "Dry run report"));
        const grid = el("div", "apdf-report-grid");
        const stat = (label: string, value: string): void => {
          const cell = el("div", "apdf-stat");
          cell.appendChild(el("div", "apdf-stat-val", value));
          cell.appendChild(el("div", "apdf-stat-label", label));
          grid.appendChild(cell);
        };
        stat("Ranges found", String(r.rangesFound.length));
        stat("Products to import", String(r.productCount));
        stat("Rows dropped", String(r.errorCount));
        rep.appendChild(grid);

        if (r.rangesFound.length) {
          rep.appendChild(el("div", "apdf-report-line", `Ranges: ${r.rangesFound.join(", ")}`));
        }
        if (r.dataQuality.length) {
          const dq = el("ul", "apdf-dq");
          for (const issue of r.dataQuality) dq.appendChild(el("li", undefined, issue));
          rep.appendChild(dq);
        }
        if (r.sampleErrors.length) {
          const det = el("details", "apdf-errors");
          det.appendChild(el("summary", undefined, `${r.errorCount} dropped row${r.errorCount === 1 ? "" : "s"}`));
          const ul = el("ul");
          for (const e of r.sampleErrors) ul.appendChild(el("li", undefined, `Row ${e.rowNum}: ${e.reason}`));
          det.appendChild(ul);
          rep.appendChild(det);
        }
        if (dryPassed) {
          rep.appendChild(el("div", "apdf-pass", "✓ Dry run passed — Confirm & Import is now enabled."));
        }
        card.appendChild(rep);
      }

      // Import result
      if (importResult) {
        const ok = el("div", "apdf-result");
        ok.appendChild(el("div", "apdf-result-title", "✓ Import complete"));
        ok.appendChild(el("div", undefined,
          `${importResult.count} product${importResult.count === 1 ? "" : "s"} written to ${data.supplierName}'s catalogue.`));
        if (importResult.errorCount) {
          ok.appendChild(el("div", "apdf-warn", `${importResult.errorCount} row(s) failed to write.`));
        }
        card.appendChild(ok);
      }

      return card;
    }

    // Update Stage-1 button enabled state without a full re-render (keeps focus).
    function syncButtons(): void {
      const extractBtn = container.querySelector<HTMLButtonElement>(".apdf-extract");
      if (extractBtn) extractBtn.disabled = extracting || !file || !supplierId || !supplierName.trim();
    }

    // ── Actions ────────────────────────────────────────────────────────────────
    function readInstructions(): string {
      const ins = container.querySelector<HTMLTextAreaElement>("#apdf-ins");
      return ins ? ins.value.trim() : "";
    }

    async function runExtract(): Promise<void> {
      if (extracting || !file || !supplierId || !supplierName.trim()) return;
      extracting = true;
      resetAfterExtract();
      render();
      try {
        const fd = new FormData();
        fd.append("supplierName", supplierName.trim());
        fd.append("columns", JSON.stringify(Array.from(selected)));
        fd.append("instructions", readInstructions());
        fd.append("file", file, file.name);

        const res = await fetch("/api/admin/pdf-import/extract", {
          method: "POST",
          credentials: "include",
          body: fd,
          signal: ac.signal,
        });
        if (!res.ok) {
          const txt = await res.text().catch(() => "");
          let msg = txt || `HTTP ${res.status}`;
          try { const j = JSON.parse(txt); if (j.error) msg = j.error; } catch { /* keep text */ }
          throw new Error(msg);
        }
        extracted = (await res.json()) as ExtractResp;
        ctx.showToast(`Extracted ${extracted.count} product(s)`, "success");
      } catch (err) {
        if ((err as Error).name === "AbortError") return;
        ctx.showToast(`Extraction failed: ${(err as Error).message}`, "error");
      } finally {
        extracting = false;
        render();
      }
    }

    function downloadCsv(): void {
      if (!extracted) return;
      const csv = buildCsv(extracted.products, extracted.columns);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${slugify(extracted.supplierName)}-catalogue.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    }

    async function runDry(): Promise<void> {
      if (!extracted || dryRunning) return;
      dryRunning = true;
      dryReport = null;
      dryPassed = false;
      render();
      try {
        const resp = await api<{ ok: boolean; report: DryReport }>("/admin/pdf-import/dry-run", {
          method: "POST",
          signal: ac.signal,
          body: JSON.stringify({
            products: extracted.products,
            supplierName: extracted.supplierName,
            supplierId,
          }),
        });
        dryReport = resp.report;
        dryPassed = resp.report.productCount > 0;
        if (dryPassed) ctx.showToast("Dry run passed", "success");
        else ctx.showToast("Dry run produced no valid rows", "error");
      } catch (err) {
        if ((err as Error).name === "AbortError") return;
        ctx.showToast(`Dry run failed: ${(err as Error).message}`, "error");
      } finally {
        dryRunning = false;
        render();
      }
    }

    async function runImport(): Promise<void> {
      if (!extracted || !dryPassed || importing) return;
      importing = true;
      render();
      try {
        const resp = await api<ImportResp>("/admin/pdf-import/import", {
          method: "POST",
          signal: ac.signal,
          body: JSON.stringify({
            products: extracted.products,
            supplierName: extracted.supplierName,
            supplierId,
            filename: file?.name,
          }),
        });
        importResult = resp;
        // One import per dry run — re-arm so a repeat needs a fresh dry run.
        dryPassed = false;
        ctx.showToast(`Imported ${resp.count} product(s)`, "success");
      } catch (err) {
        if ((err as Error).name === "AbortError") return;
        ctx.showToast(`Import failed: ${(err as Error).message}`, "error");
      } finally {
        importing = false;
        render();
      }
    }

    // ── Init ───────────────────────────────────────────────────────────────────
    render();
    void (async () => {
      try {
        const resp = await api<{ suppliers: SupplierOpt[] }>("/admin/pdf-import/suppliers", { signal: ac.signal });
        suppliers = resp.suppliers || [];
        render();
      } catch (err) {
        if ((err as Error).name === "AbortError") return;
        ctx.showToast("Failed to load suppliers", "error");
      }
    })();

    return () => {
      ac.abort();
    };
  },
};

registerPage(page);
